import { TOKENS } from '@/styles/theme.constants';
import { useWebchatStore } from '@/store/webchat.store';
import { WebchatConversation } from '@/services/webchat-service/webchat.service.types';
import { Badge, Input } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { SessionCard } from '../session-card/session-card';
import styles from './session-list.module.scss';

// 'bot' covers three flavors of "AI is handling this right now":
//   - ChatConversation in pre-claim state (webchat bot)
//   - AnalyticsCall (legacy RAI voice — qdrant/LLM bot)
//   - ElevenLabsCall pre-claim
// "AI handling" reads better than "Bot" when a Voice card sits under it.
const STATUS_CONFIG = {
  queued: { labelKey: 'status.queued', color: '#fa8c16' },
  live: { labelKey: 'status.live', color: '#52c41a' },
  bot: { labelKey: 'sessionList.groups.bot', color: '#1890ff' },
} as const satisfies Record<string, { labelKey: ParseKeys<'webchat'>; color: string }>;

const STATUS_ORDER: (keyof typeof STATUS_CONFIG)[] = ['queued', 'live', 'bot'];

export const SessionList = () => {
  const { t } = useTranslation('webchat');
  const sessions = useWebchatStore((s) => s.sessions);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return sessions;
    const q = search.toLowerCase();
    return sessions.filter(
      (s) =>
        s.visitor_name?.toLowerCase().includes(q) ||
        s.visitor_phone?.toLowerCase().includes(q) ||
        s.sid?.toLowerCase().includes(q)
    );
  }, [sessions, search]);

  const grouped = useMemo(() => {
    const groups: Record<string, WebchatConversation[]> = {};
    for (const status of STATUS_ORDER) {
      // Queued cards sort by created_at ascending so the oldest hold
      // bubbles to the top — that's the caller who's been waiting
      // longest and should be picked up first (fair-order queueing).
      // Live + bot keep insertion order (no claim race to bias).
      const items = filtered.filter((s) => s.webchat_status === status);
      groups[status] = status === 'queued'
        ? [...items].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        : items;
    }
    return groups;
  }, [filtered]);

  return (
    <div className={styles.sessionList}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>{t('sessionList.title')}</span>
        {sessions.length > 0 && (
          <Badge
            count={sessions.length}
            overflowCount={99}
            style={{ backgroundColor: TOKENS.brand }}
            size="small"
          />
        )}
      </div>
      <div className={styles.searchWrapper}>
        <Input.Search
          placeholder={t('sessionList.searchPlaceholder')}
          allowClear
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className={styles.scrollArea}>
        {/*
          Flat list: every card is a direct child of scrollArea. The
          previous shape wrapped each status group in its own
          `<div key={status}>` — which meant when a card transitioned
          from `bot` to `queued`, its parent in the React tree changed.
          React doesn't preserve mounted state across parents even when
          the child's own key is stable, so the card unmounted from the
          bot group and re-mounted in the queued group on every WS
          status broadcast. That re-mount re-fired every effect
          (OnHoldIndicator's setInterval), re-injected Antd Badge/Tag
          styles, and produced a brief whole-card flash that read as a
          page-wide flicker when several cards transitioned at once.
          Keeping the parent stable (scrollArea) and using session.id
          as the only key means React reconciles position changes by
          reordering DOM nodes in place — no mount/unmount cycle.
        */}
        {STATUS_ORDER.flatMap((status) => {
          const items = grouped[status];
          if (!items.length) return [];
          return [
            <div key={`header-${status}`} className={styles.groupHeader}>
              {t(STATUS_CONFIG[status].labelKey)}
              <Badge
                count={items.length}
                style={{ backgroundColor: STATUS_CONFIG[status].color }}
                size="small"
              />
            </div>,
            ...items.map((session) => (
              <SessionCard key={session.id} session={session} />
            )),
          ];
        })}
        {filtered.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyTitle}>
              {search ? t('sessionList.empty.noMatches') : t('sessionList.empty.noSessions')}
            </div>
            <div className={styles.emptyHint}>
              {search
                ? t('sessionList.empty.noMatchesHint')
                : t('sessionList.empty.noSessionsHint')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
